import { useEffect, useMemo } from 'react';
import { StripeDisputeStatus, useFindManyDisputesCountQuery } from 'shared/types/graphql';
import useClinicUser from './useClinicUser';

interface DisputeNotificationOutput {
  showDisputeNotification: boolean;
  count: number;
  refetchDisputeNotification: () => void;
}

export const useDisputeNotification = (): DisputeNotificationOutput => {
  const { currentClinic, hasAdminRoleAtCurrentClinic } = useClinicUser();

  const isPaymentsEnabled = useMemo(
    () => !!currentClinic?.submerchantStripeId && !!currentClinic.submerchantStripeChargesEnabled,
    [currentClinic],
  );

  const isCarePaymentProcessingClinic = useMemo(
    () => currentClinic?.id === currentClinic?.carePlanProviderGroup?.paymentProcessorClinicId,
    [currentClinic?.carePlanProviderGroup?.paymentProcessorClinicId, currentClinic?.id],
  );

  const { data: disputesData, refetch: refetchDisputes } = useFindManyDisputesCountQuery({
    variables: {
      where: {
        OR: [
          {
            stripePaymentIntent: {
              clinicId: {
                equals: currentClinic?.id,
              },
            },
          },
          ...(isCarePaymentProcessingClinic
            ? [
                {
                  careEnrollmentPayment: {
                    stripeInvoice: {
                      careStripeSubscription: {
                        associatedCarePlanProviderGroup: {
                          participatingClinics: {
                            some: {
                              id: {
                                equals: currentClinic?.id,
                              },
                            },
                          },
                        },
                      },
                    },
                  },
                },
              ]
            : []),
        ],
        status: {
          in: [StripeDisputeStatus.NeedsResponse, StripeDisputeStatus.WarningNeedsResponse],
        },
      },
    },
    skip: !currentClinic?.id || !isPaymentsEnabled || !hasAdminRoleAtCurrentClinic,
  });

  const count = useMemo(() => disputesData?.findManyStripeDisputeCount || 0, [disputesData]);

  useEffect(() => {
    if (!isPaymentsEnabled && !hasAdminRoleAtCurrentClinic) {
      return;
    }

    const THREE_MINUTES_IN_MS = 1000 * 60 * 3;
    const interval = setInterval(() => {
      refetchDisputes();
    }, THREE_MINUTES_IN_MS);
    return (): void => clearInterval(interval);
  }, [hasAdminRoleAtCurrentClinic, isPaymentsEnabled, refetchDisputes]);

  return {
    count,
    showDisputeNotification: hasAdminRoleAtCurrentClinic && isPaymentsEnabled && count > 0,
    refetchDisputeNotification: refetchDisputes,
  };
};
